import { getUserFiles } from '@/uppy-file-upload/fetch/get-user-files'
import type { UserFileRead } from '@/uppy-file-upload/schemas'
import type { AppDispatch } from '@/store/store'
import { UserFilesActions } from '../store/slice'

/**
 * Fetch user files by id and write them to the store, always re-fetching from the backend even when
 * a record is already cached. Use this when the cached copy may be out of date and you need the
 * latest — e.g. polling a file's processing status, or refreshing after a rename. For a one-shot
 * read where a cached record is fine, prefer `resolveUserFile` (utils/resolve-user-files). Inside
 * React, prefer the `useUserFiles` hook.
 */
export async function fetchAndStoreUserFiles(ids: string[], dispatch: AppDispatch): Promise<Record<string, UserFileRead>> {
  const uniqueIds = [...new Set(ids)]
  if (uniqueIds.length === 0) return {}

  const response = await getUserFiles(uniqueIds)
  const records = uniqueIds.map((id) => response.files[id]?.record).filter((record): record is UserFileRead => Boolean(record))
  if (records.length > 0) dispatch(UserFilesActions.upsertUserFiles(records))
  const unavailableIds = uniqueIds.filter((id) => !response.files[id]?.record)
  if (unavailableIds.length > 0) dispatch(UserFilesActions.markUserFilesUnavailable(unavailableIds))

  return Object.fromEntries(records.map((record) => [record.id, record]))
}
